/**
 * THE STUDIO TYPE SCALE — five steps, derived from what the Studio already is.
 *
 * The stage surfaces were built at different times and each picked its own sizes, so type jumped as the
 * creator walked the journey (owner 2026-08-23: "keep font sizes consistent to a devised system, Story tab
 * seems to have bigger fonts"). This scale was not invented from taste: it is a census of every font size in
 * `src/components/studio` (2026-08-23), keeping the size that already DOMINATES each role and retiring the
 * one-offs.
 *
 *   11px  78 uses   ·  12px  69 uses  ·  14px  40 uses  ·  10px  33 uses
 *   everything else appeared at most twice: 8px, 9px, 13px, 15px, 16px, 18px, 20px, 24px, 30px.
 *
 * The first pass read that census as 14 / 12 / 11 / 10 plus a 16px page title. It made the Studio consistent,
 * which was the point, but it kept the Studio a step larger than everything around it.
 *
 * DENSITY PASS 2026-08-23 (owner, screenshot96: *"see how vscode fonts allow for a lot of info?"*). The first
 * pass made the Studio CONSISTENT but left it a step too large — 16px titles over 14px prose. VS Code is the
 * reference the owner named, and its whole interface runs at ONE size: 13px for every tree row, tab, and
 * label, 11px for section headers, and no page title above the UI font at all. That is why a 1440px window
 * holds an explorer, an editor, a terminal and a status bar at once.
 *
 * So the scale drops one step and lands on 13px as the WORKSTATION BODY — which is not a new number: the left
 * explorer, the chat rail, and the chat bubbles were already 13px. The Studio was the only surface speaking
 * 14/16, so this is the Studio joining the workstation rather than the workstation moving.
 *
 * ROLES (use the role, not a raw size — that is the point of the file): the current values and the two registers
 * are documented beside TYPE below; the sizes in this history note are the 2026-08-23 ones, superseded 2026-10-09.
 *
 * The FLOOR is now 11px (it was 10px). Anything smaller is not readable at a workstation distance; the only survivors below it
 * are single-letter avatar initials sized to their circle, which are glyphs rather than text.
 *
 * Weight, not size, carries the hierarchy between `sectionTitle` and `body` — a semibold 13px heading over
 * 13px prose. Density comes from the SPACING scale too: type this size wants tighter padding, or the page
 * only looks emptier (see `spacing.ts`).
 */

/*
 * TWO REGISTERS (owner decision 2026-10-09, designed 2026-10-21 in claude-design "Two Registers", option 1a).
 *
 * The density pass above made every surface 13px like VS Code. That suits the workshop CHROME, but the creator's
 * own words (plot, episode story, script beats, dialogue, character notes) were set like a code editor's sidebar,
 * and our audience is patient authors who read and write books, often past forty. So there are now two registers:
 *
 *   CHROME (Inter, compact, floor raised): stageTitle 15 · sectionTitle/body 14 · control 13 · label/meta 12 in
 *   sentence case (no uppercase micro labels: they read as technical) · micro 11, the hard floor.
 *   MANUSCRIPT (Literata): manuscript 17px / 1.72 for anything the creator writes or reads as story,
 *   manuscriptTitle 28px for an episode or series title on its own page, dialogue in italic Literata.
 */
// In rem (16px = 1rem), so the reader's Text size setting (Settings > Display & reading) scales both registers:
// 15 / 14 / 13 / 12 / 11px chrome and 17 / 28px manuscript at the default size (TYPE-3, 2026-10-21).
export const TYPE = {
  stageTitle: 'text-[0.9375rem] font-semibold tracking-tight',
  sectionTitle: 'text-sm font-semibold',
  body: 'text-sm',
  control: 'text-[0.8125rem]',
  label: 'text-xs font-semibold',
  meta: 'text-xs',
  micro: 'text-[0.6875rem]',
  manuscript: 'font-manuscript text-[1.0625rem] leading-[1.72]',
  manuscriptTitle: 'font-manuscript text-[1.75rem] font-semibold leading-tight',
  dialogue: 'font-manuscript italic text-[1.0625rem] leading-[1.72]',
} as const;

export type TypeRole = keyof typeof TYPE;
